import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { BasketQtyComponentDef } from '@df/basket/view/basket-qty.component.def';
import { App } from '@df/core/app';
import { DfEventService } from '@df/core/service/df-event';
import { AbService } from '@df/module-ab/ab.service';
import { TranslationService } from '@df/ui/translation/translation.service';
import { Subscription } from 'rxjs/internal/Subscription';
import { BasketDecorator } from '../customer/api/basket/basket.decorator';

export const ANIMATE_BASKET_AB_CONFIG = {
    showFor: 'B',
    experimentName: 'animateBasket'
};

@Component({
    selector: '[basketQty]',
    templateUrl: './basket-qty.html',
    styleUrl: './basket-qty.scss',
    host: {
        '[class]': 'classNames()',
        '[class.is-animate]': 'isAnimate',
        '[attr.aria-label]': 'ariaLabel',
        '[attr.bullet]': 'currentQtyComputed() || undefined',
        '(click)': 'handleClick()'
    },
    standalone: true,
    imports: [CommonModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BasketQtyComponent extends BasketQtyComponentDef {
    readonly className = input<string>('', { alias: 'class' });

    protected readonly classNames = computed(() => this.className() + ' ' + 'pos-relative basket-qty');
    protected readonly currentQtyComputed = computed(() => this.qtySignal());

    protected app = inject(App);
    protected abService = inject(AbService);
    protected dfEventService = inject(DfEventService);
    protected translationService = inject(TranslationService);

    protected abServiceSubscription?: Subscription;

    protected isAnimate = false;
    protected ariaLabel = '';
    protected qtySignal = signal(0);

    override get basket(): BasketDecorator {
        return <BasketDecorator>this.app.customer.basket;
    }

    override ngOnInit() {
        super.ngOnInit();

        // Initialize qty from current basket data
        this.qtySignal.set(this.basket?.qty || 0);

        this.abServiceSubscription?.unsubscribe();

        this.abServiceSubscription = this.abService.getSubject(ANIMATE_BASKET_AB_CONFIG.experimentName).subscribe(value => {
            if (value.variant === ANIMATE_BASKET_AB_CONFIG.showFor) {
                this.isAnimate = true;
            } else {
                this.isAnimate = false;
            }

            this.detectChanges();
        });

        // Listen to basketDataChange events
        this.dfEventService.success(event => {
            if (event.id === 'basketDataChange') {
                // Update qty directly from the event data
                this.qtySignal.set(event.data.after?.qty || 0);
                this.updateAriaLabel();
            }
        });

        this.updateAriaLabel();
    }

    override ngOnDestroy() {
        super.ngOnDestroy();

        this.abServiceSubscription?.unsubscribe();
    }

    protected handleClick() {
        this.isAnimate = false;

        this.detectChanges();
    }

    private updateAriaLabel() {
        this.ariaLabel = `${this.translationService.transform('Basket quantity %', this.qtySignal())} ${this.translationService.transform('click to open minibasket')}`;
        this.detectChanges();
    }
}
